/**
 * Rule-based layman explanations for proposal line items (no LLM).
 * 43P: Not rendered in customer-facing proposal UI until we support reliable line-level context
 * (catalogue entry, parsed Excel row, manually approved copy). Keep for future use.
 */
import { audioClassLayman } from "@shared/proposalCleoProductClass";

export type ProposalLineExplainInput = {
  category?: string | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  rawText?: string | null;
  /** Primary title from description snapshot */
  title?: string | null;
  /** Brand / model line */
  brandModel?: string | null;
};

function normalizeHaystack(input: ProposalLineExplainInput): string {
  const parts = [
    input.category,
    input.name,
    input.brand,
    input.model,
    input.title,
    input.brandModel,
    input.rawText,
  ]
    .map((s) => String(s ?? "").toLowerCase())
    .filter(Boolean);
  return parts.join(" ");
}

function cleanSnippet(text: string, maxLen: number): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1)).replace(/\s+\S*$/, "")}…`;
}

type Rule = {
  test: (h: string) => boolean;
  what: string;
  does: string;
  why: string;
};

const RULES: Rule[] = [
  {
    test: (h) => /(wifi|wi-fi|wireless ap|access point|\bap\b|mesh|wlan)/i.test(h) && !/cctv|camera/i.test(h),
    what: "WiFi access point or wireless coverage device",
    does: "Distributes WiFi signal across your space so phones, tablets, and smart devices get strong, stable internet where you need it.",
    why: "Used when the proposal calls for reliable wireless coverage in that area of the home or site.",
  },
  {
    test: (h) => /(network switch|ethernet switch|\bswitch\b.*port|poe switch|managed switch)/i.test(h),
    what: "Network switch",
    does: "Connects wired devices together so they can share the network reliably, often with dedicated ports for TVs, access points, or controllers.",
    why: "Included when multiple wired devices need a stable backbone beyond a single router port.",
  },
  {
    test: (h) => /(\brouter\b|\bgateway\b|\bont\b|fiber modem)/i.test(h),
    what: "Router or internet gateway",
    does: "Brings internet into your network and shares it between wired and wireless devices.",
    why: "Typically the main connection point between your service provider and your internal network.",
  },
  {
    test: (h) => /(hdmi|\bav matrix\b|video matrix|hdmi matrix|matrix switcher)/i.test(h),
    what: "HDMI or AV matrix",
    does: "Routes video (and sometimes audio) from several sources to the right TVs or projectors without swapping cables manually.",
    why: "Used when you want multiple sources shared across several displays in a clean, controlled way.",
  },
  {
    test: (h) => audioClassLayman(h)?.what === "Preamplifier",
    what: "Preamplifier",
    does: "Lets you choose sources and set listening level, then sends a line-level signal to a power amplifier. It does not power the speakers itself.",
    why: "Used as the control stage in front of the power amplifier in this system.",
  },
  {
    test: (h) => audioClassLayman(h)?.what === "Power amplifier",
    what: "Power amplifier",
    does: "Takes the line-level signal from a preamp (or similar) and powers your speakers.",
    why: "Chosen to match your speaker layout and listening level for that room.",
  },
  {
    test: (h) => {
      const layman = audioClassLayman(h);
      return Boolean(layman) && layman?.what === "Integrated amplifier or AV receiver";
    },
    what: "Integrated amplifier or AV receiver",
    does: "Powers your speakers and often switches between music or movie sources at controlled volume levels.",
    why: "Chosen to match your speaker layout and listening preferences for that room.",
  },
  {
    test: (h) => /(speaker|subwoofer|bookshelf|floorstand|in-wall speaker|in-ceiling speaker)/i.test(h),
    what: "Loudspeaker",
    does: "Converts electrical audio signals into sound you hear — stereo, surround, or dedicated bass for subwoofers.",
    why: "Specified for the room size, layout, and performance level in this proposal.",
  },
  {
    test: (h) => /(projector|projection screen|motorised screen|fixed frame screen)/i.test(h),
    what: "Projector or projection screen",
    does: "Displays a large image for movies and presentations; the screen surface is matched to the projector for a clear picture.",
    why: "Included when a cinema-style or large-format viewing experience is part of the design.",
  },
  {
    test: (h) => /(lighting control|dimmer|lutron|knx.*light|scene keypad|smart light)/i.test(h),
    what: "Lighting control component",
    does: "Lets you dim lights, recall scenes, or automate lighting to suit daily use and mood.",
    why: "Part of the lighting or smart-home scope described in this proposal.",
  },
  {
    test: (h) => /(camera|cctv|nvr|dvr|surveillance|ip cam)/i.test(h),
    what: "Surveillance camera or recorder",
    does: "Captures or stores video for security monitoring around the property.",
    why: "Listed where monitoring or recording that area is part of the agreed scope.",
  },
  {
    test: (h) => /(intercom|door station|video doorbell|gate station)/i.test(h),
    what: "Intercom or door entry device",
    does: "Lets you see or speak with visitors and release the door or gate when appropriate.",
    why: "Included for entry communication as specified in the proposal.",
  },
  {
    test: (h) => /(touch panel|touchscreen|control panel|crestron|savant|control4|rti)/i.test(h),
    what: "Touch control panel",
    does: "Provides a simple screen to control audio, video, lighting, or other systems in one place.",
    why: "Used so day-to-day control of the system is easy for everyone in the household.",
  },
  {
    test: (h) => /(nas|network storage|server rack|ups\b|battery backup)/i.test(h),
    what: "Storage or power resilience component",
    does: "Stores files or keeps critical gear running briefly during a power blip, depending on the exact item.",
    why: "Specified where backups or uptime matter for the proposed system.",
  },
  {
    test: (h) => /(cable|patch cord|hdmi cable|speaker cable|cat6|ethernet cable|fiber patch)/i.test(h),
    what: "Cable or interconnect",
    does: "Carries signal or power between devices using the correct type and length for reliable performance.",
    why: "Required to connect the equipment in this line to the rest of the system.",
  },
  {
    test: (h) => /(rack|cabinet|enclosure|shelf)/i.test(h) && /(network|av|equipment|it)/i.test(h),
    what: "Equipment rack or cabinet",
    does: "Holds and organizes gear so wiring stays tidy, ventilated, and serviceable.",
    why: "Keeps the installation neat and maintainable when multiple devices are installed together.",
  },
];

export type LaymanLineExplain = {
  whatItIs: string;
  whatItDoes: string;
  whyItIsUsed: string;
};

export function explainProposalLineForCustomer(input: ProposalLineExplainInput): LaymanLineExplain {
  const haystack = normalizeHaystack(input);
  for (const rule of RULES) {
    if (rule.test(haystack)) {
      return { whatItIs: rule.what, whatItDoes: rule.does, whyItIsUsed: rule.why };
    }
  }

  const label =
    [input.title, input.name, input.brandModel].map((s) => String(s ?? "").trim()).find(Boolean) ||
    cleanSnippet(String(input.category ?? ""), 80) ||
    "This line item";

  const fromRaw = cleanSnippet(String(input.rawText ?? ""), 220);
  const cat = String(input.category ?? "").trim();

  return {
    whatItIs: label,
    whatItDoes: fromRaw
      ? fromRaw
      : "A component listed on this proposal. Your integrator can clarify exact model behaviour on site.",
    whyItIsUsed: cat
      ? `Included under your ${cat} scope as shown on this proposal.`
      : "Included as part of the proposed system in this document.",
  };
}

/**
 * 43Q: Sales-catalogue retail lines only — returns null when there is no grounded description
 * (rules, or stored line text ≥ 20 chars). No generic “component listed…” filler.
 */
export function explainRetailCatalogueLineTooltip(input: ProposalLineExplainInput): LaymanLineExplain | null {
  const haystack = normalizeHaystack(input);
  for (const rule of RULES) {
    if (rule.test(haystack)) {
      return { whatItIs: rule.what, whatItDoes: rule.does, whyItIsUsed: rule.why };
    }
  }
  const fromRaw = cleanSnippet(String(input.rawText ?? ""), 400);
  if (fromRaw.length < 20) return null;
  const title =
    [input.title, input.name, input.brandModel].map((s) => String(s ?? "").trim()).find(Boolean) ?? "";
  const cat = String(input.category ?? "").trim();
  const whatItIs = title || (cat ? cleanSnippet(cat, 80) : "");
  if (!whatItIs) return null;
  return {
    whatItIs,
    whatItDoes: fromRaw,
    whyItIsUsed: cat ? `Listed under ${cat}.` : "From the stored catalogue line text.",
  };
}
